import { Button } from '@/components/Button';
import { Callout } from '@/components/Callout';
import { DeadEndHelpButton } from '@/components/DeadEndHelpButton';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { StatusBadge } from '@/components/StatusBadge';
import { useTranslation } from '@/i18n/useTranslation';
import { useKioskSession } from '@/lib/kioskSession';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

import { useCountdown } from '../hooks/useCountdown';
import { formatRemaining } from '../lib/formatRemaining';

/**
 * The throttle countdown [decision 0064].
 *
 * ⚠️ **This screen has NO Figma frame, and none is owed to an oversight — it is owed to design.** Decision
 * 0064 is dated 2026-08-07, after the kiosk page was drawn; neither it nor `eval-kid-6` names a design
 * oracle, and no wait or countdown frame exists anywhere on the kiosk page. It is therefore assembled from
 * the design system's own card, type scale, callout and button rather than invented wholesale — but the
 * layout is a proposal, not a read value, and it should be replaced when the frame lands.
 *
 * **It is why the throttle is not a dead end.** Decision 0063 said the form becomes unavailable and the
 * candidate must contact a recruiter with no self-recovery — at an unattended kiosk that is the dead end the
 * convention skill § 6 calls a defect. Here the honest candidate who mistyped waits three minutes and
 * recovers alone; the recruiter appears only at the 60-minute ceiling, and even then IN ADDITION to the
 * countdown rather than instead of it.
 *
 * It says nothing about whether a record was found. A registered and an unregistered email reach this screen
 * identically — otherwise the control would have become an account-existence oracle.
 */
export function ThrottledScreen() {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const throttle = useKioskSession((state) => state.throttle);
  const returnToCheckIn = useKioskSession((state) => state.returnToCheckIn);

  const remaining = useCountdown(throttle?.retryAfterSeconds ?? 0);
  const isOver = remaining <= 0;

  return (
    // The lockout is a sign-in dead end: *Need help?* here reads *stuck at sign-in* [0168; 0461].
    <KioskScreen signInDeadEnd>
      <KioskCard
        footer={
          /*
            The screen's own help control sits above *Try again*, stacked full width like it [KID-7; 0230]. No
            frame exists for either, so this is the design system's own stack, not a read layout.
          */
          <div className="flex w-full flex-col gap-4">
            <DeadEndHelpButton fullWidth />
            <Button disabled={!isOver} fullWidth onClick={returnToCheckIn}>
              {t('throttle.tryAgain')}
            </Button>
          </div>
        }
        header={<StatusBadge label={t('badge.kiosk')} />}
      >
        <div className="flex w-full flex-col items-start gap-6 pb-8">
          <div className="flex w-full flex-col items-start gap-2">
            <h1
              className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
              ref={heading}
              tabIndex={-1}
            >
              {t('throttle.heading')}
            </h1>
            <p className="w-full text-sm leading-6 text-[var(--muted-foreground)]">
              {t('throttle.body')}
            </p>
          </div>

          {/*
            `role="timer"` with `aria-live="off"`: a countdown that announced every second would make the
            screen unusable with a screen reader. The remaining time is readable on demand, and the label
            names what the number is — never position or colour alone.
          */}
          <p
            aria-label={t('throttle.remaining')}
            aria-live="off"
            className="w-full text-center text-5xl font-bold tabular-nums tracking-[-0.36px] text-[var(--foreground)]"
            role="timer"
          >
            {formatRemaining(remaining)}
          </p>

          {throttle?.contactRecruiter === true && <Callout>{t('throttle.recruiter')}</Callout>}
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
